import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Lock, ArrowRight, Loader2, Shield } from 'lucide-react';
import api from '@/api';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function AdminLogin({ onBack, onAdminAuth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const captchaContainerId = useRef(`recaptcha-admin-${Date.now()}`);
  const captchaWidgetId = useRef(null);

  useEffect(() => {
    setCaptchaFailed(false);
    let attempts = 0;
    const tryRender = () => {
      const el = document.getElementById(captchaContainerId.current);
      if (!el) return false;
      if (captchaWidgetId.current !== null) return true;
      if (!window.__recaptchaReady || !window.grecaptcha || !window.grecaptcha.render) return false;
      try {
        captchaWidgetId.current = window.grecaptcha.render(el, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => { setCaptchaToken(token); setCaptchaFailed(false); },
          'expired-callback': () => setCaptchaToken(null),
          'error-callback': () => setCaptchaFailed(true),
          theme: 'dark',
          size: 'normal',
        });
        setCaptchaFailed(false);
        return true;
      } catch (e) {
        console.error('[reCAPTCHA] render error:', e);
        setCaptchaFailed(true);
        return true;
      }
    };
    const interval = setInterval(() => {
      attempts++;
      if (tryRender() || attempts > 40) {
        if (attempts > 40 && captchaWidgetId.current === null) setCaptchaFailed(true);
        clearInterval(interval);
      }
    }, 200);
    return () => {
      clearInterval(interval);
      if (captchaWidgetId.current !== null && window.grecaptcha && window.grecaptcha.reset) {
        try { window.grecaptcha.reset(captchaWidgetId.current); } catch (e) { /* ignore */ }
      }
      captchaWidgetId.current = null;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    if (!captchaToken) { setError(captchaFailed ? 'CAPTCHA could not load — check domain authorization' : 'Please complete the CAPTCHA checkbox'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.adminLogin({ email: email.trim(), password, captcha_token: captchaToken });
      localStorage.setItem('wtf_admin_token', res.token);
      onAdminAuth();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid admin credentials');
      // Reset captcha on failure
      if (window.grecaptcha && captchaWidgetId.current !== null) {
        window.grecaptcha.reset(captchaWidgetId.current);
        setCaptchaToken(null);
      }
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen flex items-center justify-center px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <button data-testid="admin-back" onClick={onBack}
          className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center mb-6 hover:bg-gray-200 transition-colors">
          <ArrowLeft size={18} className="text-gray-600" />
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gray-900 text-white flex items-center justify-center mx-auto mb-4">
            <Shield size={28} />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900">Admin Access</h1>
          <p className="text-sm text-gray-400 mt-1">Restricted area</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Admin email"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 transition-all" />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="admin-password" type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-400 transition-all" />
          </div>

          <div className="flex flex-col items-center py-2">
            <div id={captchaContainerId.current} data-testid="admin-captcha"></div>
            {captchaFailed && (
              <div className="mt-2 px-4 py-2 rounded-lg bg-amber-900/30 border border-amber-700/40 text-center">
                <p className="text-amber-400 text-xs font-semibold">CAPTCHA failed to load</p>
                <p className="text-amber-400/70 text-[10px] mt-0.5">Check domain authorization in reCAPTCHA console</p>
              </div>
            )}
          </div>

          {error && <p data-testid="admin-login-error" className="text-red-500 text-xs text-center">{error}</p>}
          <button data-testid="admin-submit" type="submit" disabled={loading}
            className="w-full py-3.5 rounded-xl bg-gray-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-60 transition-all">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <>Sign In <ArrowRight size={16} /></>}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
